import { useCallback, useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Download, ArrowLeft, Sparkles } from "lucide-react";

const SITE = "reprehendo.viglietron.com";
const LINK = `https://${SITE}/prova`;
const linkFor = (src) => `${LINK}?src=${src}`;
const GOLD = "#f59e0b";
const INK = "#080d1a";

/** Cartoncino 90×55 mm: fronte impatto, tre passi, QR. Se c'è un locale, stampa anche accesso. */
const Card = ({ qr, d }) => (
  <div className="card relative flex overflow-hidden" style={{ width: "90mm", height: "55mm", background: INK, color: "#fff" }}>
    <div className="pointer-events-none absolute -right-8 -top-10 h-24 w-24 rounded-full"
         style={{ background: "radial-gradient(circle, rgba(245,158,11,.28), transparent 70%)" }} />
    <div className="flex min-w-0 flex-1 flex-col justify-between p-[5mm]">
      <div>
        <p className="text-[9pt] font-bold leading-none tracking-tight">
          Reprehendo <span style={{ color: GOLD, fontSize: "5pt", letterSpacing: "1px" }}>RH</span>
        </p>
        <p className="mt-[2mm] text-[10.5pt] font-bold leading-[1.15]">
          Il gestionale che fa<br />respirare il tuo locale.
        </p>
      </div>
      <div className="space-y-[0.8mm]">
        {["Inquadra il codice", "Rispondi a 3 domande", "Il tuo locale è pronto"].map((s, i) => (
          <p key={s} className="text-[6pt] leading-none text-slate-300">
            <span style={{ color: GOLD, fontWeight: 700 }}>{i + 1}.</span> {s}
          </p>
        ))}
      </div>
      {d.name ? (
        <div className="rounded-[1mm] px-[2mm] py-[1.4mm]" style={{ background: "rgba(245,158,11,.14)" }}>
          <p className="truncate text-[6pt] font-bold leading-none" style={{ color: GOLD }}>{d.name}</p>
          <p className="mt-[0.8mm] text-[5.5pt] leading-none text-slate-200">
            Codice {d.code || "—"} · PIN {d.pin || "—"}
          </p>
        </div>
      ) : (
        <p className="text-[5.5pt] leading-none text-slate-400">Il tuo locale dentro l'app in un minuto</p>
      )}
    </div>
    <div className="flex flex-col items-center justify-center gap-[1.5mm] p-[4mm]" style={{ background: "rgba(255,255,255,.04)" }}>
      {qr && <img src={qr} alt="QR" style={{ width: "24mm", height: "24mm", borderRadius: "1mm", background: "#fff", padding: "1mm" }} />}
      <p className="text-[5pt] leading-none text-slate-400">{SITE}</p>
      {d.contact && <p className="max-w-[26mm] text-center text-[5pt] leading-tight text-slate-300">{d.contact}</p>}
    </div>
  </div>
);

export default function Biglietto() {
  const [d, setD] = useState({ name: "", code: "", pin: "", contact: "" });
  const [copies, setCopies] = useState(10);
  const [qr, setQr] = useState(null);
  const backend = process.env.REACT_APP_BACKEND_URL;
  const busy = useRef(false);

  useEffect(() => {
    const locale = new URLSearchParams(window.location.search).get("locale");
    const saved = localStorage.getItem("rh_card");
    const base = saved ? JSON.parse(saved) : {};
    setD({ name: locale || base.name || "", code: base.code || "", pin: base.pin || "", contact: base.contact || "" });
  }, []);
  useEffect(() => {
    localStorage.setItem("rh_card", JSON.stringify(d));
  }, [d]);
  useEffect(() => {
    QRCode.toDataURL(linkFor("card"), { margin: 0, width: 512, color: { dark: INK, light: "#ffffff" } }).then(setQr);
  }, []);

  const set = (k) => (e) => setD({ ...d, [k]: e.target.value });

  // Immagine verticale per WhatsApp: disegnata su canvas, così non serve nessuna libreria di screenshot
  const share = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    const W = 1080, H = 1350;
    const c = document.createElement("canvas");
    c.width = W; c.height = H;
    const x = c.getContext("2d");
    x.fillStyle = INK; x.fillRect(0, 0, W, H);
    const g = x.createRadialGradient(W, 0, 40, W, 0, 900);
    g.addColorStop(0, "rgba(245,158,11,.35)"); g.addColorStop(1, "rgba(245,158,11,0)");
    x.fillStyle = g; x.fillRect(0, 0, W, H);

    x.fillStyle = GOLD; x.font = "bold 30px Arial";
    x.fillText("REPREHENDO · GESTIONALE PER LOCALI", 90, 140);
    x.fillStyle = "#fff"; x.font = "bold 86px Arial";
    x.fillText("Il gestionale che fa", 90, 280);
    x.fillText("respirare il tuo locale.", 90, 380);
    x.fillStyle = "#cbd5e1"; x.font = "38px Arial";
    x.fillText("Sala, comande, cucina e cassa in un solo flusso.", 90, 460);

    const steps = ["Inquadra il codice qui sotto", "Rispondi a 3 domande sul tuo locale", "Entra: è già pronto, col tuo menu"];
    steps.forEach((s, i) => {
      const y = 570 + i * 78;
      x.fillStyle = GOLD; x.font = "bold 44px Arial"; x.fillText(`${i + 1}`, 90, y);
      x.fillStyle = "#e2e8f0"; x.font = "38px Arial"; x.fillText(s, 145, y);
    });

    const qrImg = new Image();
    qrImg.src = await QRCode.toDataURL(linkFor("wa"), { margin: 1, width: 640, color: { dark: INK, light: "#ffffff" } });
    await new Promise((r) => { qrImg.onload = r; });
    x.fillStyle = "#fff"; x.fillRect(360, 830, 360, 360);
    x.drawImage(qrImg, 372, 842, 336, 336);

    x.textAlign = "center";
    x.fillStyle = "#fff"; x.font = "bold 42px Arial"; x.fillText(SITE, W / 2, 1250);
    x.fillStyle = GOLD; x.font = "bold 34px Arial";
    x.fillText("Provalo stasera, sul tuo servizio", W / 2, 1305);
    if (d.contact) { x.fillStyle = "#94a3b8"; x.font = "30px Arial"; x.fillText(d.contact, W / 2, 790); }

    const a = document.createElement("a");
    a.href = c.toDataURL("image/png");
    a.download = "reprehendo-prova.png";
    a.click();
    busy.current = false;
  }, [d.contact]);

  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="biglietto-page">
      <style>{`@media print {
        body { background:#fff !important; }
        .no-print { display:none !important; }
        .sheet { display:grid !important; gap:0; padding:0; }
        .card { break-inside:avoid; -webkit-print-color-adjust:exact; print-color-adjust:exact; }
      }`}</style>

      <div className="no-print mx-auto max-w-3xl px-6 py-10">
        <a href="/" className="text-xs font-semibold text-slate-400 hover:text-amber-400" data-testid="card-back">
          <ArrowLeft className="mr-1 inline h-3 w-3" /> Torna alla pagina principale
        </a>
        <p className="mt-6 text-[11px] font-bold uppercase tracking-[0.3em] text-amber-400">Da dare in mano</p>
        <h1 className="font-head mt-2 text-4xl font-bold sm:text-5xl">Biglietto della prova</h1>
        <p className="mt-3 max-w-xl text-base text-slate-300">
          Chi lo riceve inquadra il codice e in un minuto ha il suo locale dentro l'app. Lascia i campi vuoti
          per il biglietto generico, oppure scrivi un locale per consegnarlo già pronto all'accesso.
        </p>

        <div className="mt-8 grid gap-3 sm:grid-cols-2">
          <Input className="h-12 border-slate-800 bg-slate-900 text-white placeholder:text-slate-500"
                 placeholder="Nome del locale (facoltativo)" value={d.name} onChange={set("name")} data-testid="card-name" />
          <Input className="h-12 border-slate-800 bg-slate-900 text-white placeholder:text-slate-500"
                 placeholder="Il tuo contatto (nome, telefono)" value={d.contact} onChange={set("contact")} data-testid="card-contact" />
          <Input className="h-12 border-slate-800 bg-slate-900 text-white placeholder:text-slate-500"
                 placeholder="Codice attività" value={d.code} onChange={set("code")} data-testid="card-code" />
          <Input className="h-12 border-slate-800 bg-slate-900 text-white placeholder:text-slate-500"
                 placeholder="PIN titolare" value={d.pin} onChange={set("pin")} data-testid="card-pin" />
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <span className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">Biglietti per foglio</span>
          <Input type="number" min="1" max="10" value={copies}
                 onChange={(e) => setCopies(Math.max(1, Math.min(10, Number(e.target.value) || 1)))}
                 className="h-12 w-20 border-slate-800 bg-slate-900 text-white" data-testid="card-copies" />
        </div>

        <div className="mt-6 flex flex-wrap gap-3">
          <a
            href={`${backend}/api/public/kit/biglietti.pdf?copies=${copies}&name=${encodeURIComponent(d.name)}&code=${encodeURIComponent(d.code)}&pin=${encodeURIComponent(d.pin)}&contact=${encodeURIComponent(d.contact)}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex h-14 items-center rounded-md bg-amber-500 px-7 text-base font-bold text-slate-950 transition-colors hover:bg-amber-400"
            data-testid="card-pdf-link"
          >
            <Download className="mr-2 h-5 w-5" /> Apri i biglietti in PDF
          </a>
          <a
            href={`${backend}/api/public/kit/guida.pdf`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex h-14 items-center rounded-md bg-white px-7 text-base font-bold text-slate-950 transition-colors hover:bg-slate-200"
            data-testid="guida-pdf-link"
          >
            <Download className="mr-2 h-5 w-5" /> Apri il foglio da tasca
          </a>
          <a
            href={`${backend}/api/public/kit/cassa.pdf`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex h-14 items-center rounded-md border border-slate-800 px-6 text-base font-semibold text-slate-200 transition-colors hover:bg-slate-900"
            data-testid="cassa-pdf-link"
          >
            <Download className="mr-2 h-5 w-5" /> Domande per il tecnico della cassa
          </a>
          <Button className="h-14 border border-slate-800 bg-transparent px-6 text-base font-semibold text-slate-200 hover:bg-slate-900"
                  onClick={share} data-testid="card-image-btn">
            Immagine per WhatsApp
          </Button>
        </div>
        <p className="mt-3 flex items-center gap-2 text-xs text-slate-500">
          <Sparkles className="h-3.5 w-3.5 text-amber-400" />
          Stampa su cartoncino, formato A4: i biglietti sono 90×55 mm, si tagliano con le forbici.
          Su iPhone il PDF si apre nel browser: poi Condividi per salvarlo o stamparlo.
        </p>
        <a href="/guida-demo" className="mt-4 inline-block text-sm font-semibold text-amber-400 underline hover:text-amber-300" data-testid="link-guida-demo">
          Foglio da tasca: cinque minuti a casa del cliente →
        </a>

        <p className="mt-10 text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Anteprima</p>
        <div className="mt-4 origin-top-left scale-[1.15] sm:scale-[1.4]" style={{ width: "90mm" }}>
          <Card qr={qr} d={d} />
        </div>
      </div>

      <div className="sheet mx-auto hidden" style={{ gridTemplateColumns: "90mm 90mm", width: "180mm" }} data-testid="card-sheet">
        {Array.from({ length: copies }).map((_, i) => <Card key={`copia-${i}`} qr={qr} d={d} />)}
      </div>
    </div>
  );
}
